import { useEffect, useState, ChangeEvent } from 'react';
import React from 'react';
import { FilterActionName, useDisplayPropertiesFilter, useFilterAction } from '../../state/FilterContext';

interface DisplayValueControlProps {
  name: string,
  startTrue: boolean,
  children: React.ReactNode
}

/***
 * A UI control that determines the value of a display property.
 * Display properties are properties that are added to the URL query and the sharedState
 * but that do not correspond to any properties in the metadata model.
 * It can be used for example to show/hide or highlight a part of the page based on a user action.
 */
export function DisplayValueControl({ name, startTrue, children }: DisplayValueControlProps): React.ReactElement {
  const displayProperties = useDisplayPropertiesFilter();
  const updateDisplayProperties = useFilterAction(FilterActionName.UPDATE_DISPLAY_PROPERTIES);

  const [value, setValue] = useState<boolean>(startTrue);

  useEffect(() => {
    updateDisplayProperties({ ...displayProperties, [name]: [value ? "true" : "false"] });
  }, [value, updateDisplayProperties, displayProperties, name]);

  const augmentedChildren = React.Children.map(children, child => {
    if (React.isValidElement(child)) {
      if (child.type === "checkbox") {
        child.props.onChange = (event: ChangeEvent<HTMLInputElement>) => {
          setValue(event.target.checked);
        };
        child.props.checked = value;
      }
      else if (child.type === "input") {
        child.props.onChange = (event: ChangeEvent<HTMLInputElement>) => {
          setValue(event.target.value === "true");
        };
        child.props.value = value;
        child.props.checked = value;
      }
      else {
        setValue(false);
        child.props.onMouseOver = () => { setValue(true) };
        child.props.onMouseOut = () => { setValue(false) };
      }
    }
    else {
      return child;
    }
  })

  return <>{augmentedChildren}</>;
};